import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useTRPC } from '@/lib/trpc';
import type { LeagueSummary } from '@/types/league';

/**
 * Hook to fetch all leagues the current user is a member of
 *
 * Backed by the tRPC `leagues.list` procedure via the TanStack React Query client:
 * `trpc.leagues.list.queryOptions()` produces typed query options (incl. query key)
 * that are passed straight into TanStack's `useQuery`.
 *
 * @param enabled - Whether the query should run (defaults to true)
 * @returns UseQueryResult containing leagues array, loading, and error states
 *
 * @example
 * ```tsx
 * const { data: leagues, isLoading, error } = useLeagues();
 * const leagueCount = leagues?.length ?? 0;
 * ```
 */
export type League = LeagueSummary;

export function useLeagues(enabled: boolean = true): UseQueryResult<LeagueSummary[], Error> {
  const trpc = useTRPC();
  return useQuery(
    trpc.leagues.list.queryOptions(undefined, {
      enabled,
      retry: 1, // Retry once on failure
      staleTime: 1000 * 60 * 5, // Consider data fresh for 5 minutes
      select: (data) => (data ?? []).filter((l) => l?.id && String(l.id) !== 'undefined'),
    }),
  ) as unknown as UseQueryResult<LeagueSummary[], Error>;
}

/**
 * Hook to get just the count of leagues the user is a member of
 *
 * @param enabled - Whether the query should run (defaults to true)
 * @returns Object containing league count and loading state
 *
 * @example
 * ```tsx
 * const { count, isLoading } = useLeagueCount();
 * ```
 */
export function useLeagueCount(enabled: boolean = true) {
  const { data: leagues, isLoading, error } = useLeagues(enabled);

  return {
    count: leagues?.length ?? 0,
    isLoading,
    error,
  };
}
